import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { WindowRef } from '@spartacus/core';
import { Location } from '@angular/common';
import { SdModalDefaultComponent } from '@seb/web-design-system/core';
import { CustomAuthService, TrackingService } from '../../../services';
import { IFrameResizerDirective } from '../../../directives';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'wc-modal-login',
  templateUrl: './modal-login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdModalDefaultComponent,
    IFrameResizerDirective,
    TranslatePipe
  ]
})
export class ModalLoginComponent implements OnInit, OnDestroy {
  isLoggedIn = false;

  loginUrl: SafeResourceUrl;

  loginSubscription: Subscription;
  messageSubscription: Subscription;

  isLoginModalOpened = false;
  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private authService: CustomAuthService,
    private windowRef: WindowRef,
    private location: Location,
    private domSanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef,
    private trackingService: TrackingService
  ) {}

  ngOnInit(): void {
    this.authService.toggleLogin$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.login();
    });

    this.authService
      .isUserLoggedIn()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
        this.cdRef.markForCheck();
      });
  }

  onLoginModalClose(isOpened: boolean): void {
    if (!isOpened) {
      this.loginSubscription?.unsubscribe();
      this.messageSubscription?.unsubscribe();
    }
  }

  async login(): Promise<void> {
    this.loginUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(await this.authService.getLoginUrl());
    this.isLoginModalOpened = true;
    this.trackingService.sendEvent('vpLayer', { layerName: 'login' });
    this.loginSubscription = fromEvent(this.windowRef.nativeWindow, 'message')
      .pipe(
        filter(
          (event) =>
            event['origin'] === this.windowRef.nativeWindow.location.origin && event['data'].eventId === 'oauth'
        ),

        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.trackingService.sendEvent('accountLogin', {});
        this.location.go('/' + event['data'].hash);
        this.authService.checkOAuthParamsInUrl();
        this.isLoginModalOpened = false;
        this.cdRef.markForCheck();
        this.loginSubscription.unsubscribe();
      });

    this.messageSubscription = fromEvent(this.windowRef.nativeWindow, 'message')
      .pipe(
        filter((event) => event['data'] === 'salesforce_actionbutton'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.trackingService.sendEvent('newPasswordRequest', {});
      });

    this.cdRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loginSubscription?.unsubscribe();
    this.messageSubscription?.unsubscribe();
  }
}
